// Helpers for reading error details from `supabase.functions.invoke` responses.
//
// When an Edge Function returns a non-2xx status, the JS SDK throws a
// `FunctionsHttpError` whose `.context` is the raw `Response`. The default
// `error.message` is generic ("Edge Function returned a non-2xx status code"),
// so we parse the response body ourselves to surface the real server message.

export interface EdgeErrorInfo {
  status?: number;
  code?: string;
  message: string;
  /** Raw payload returned by the function, if it was JSON. */
  body?: Record<string, unknown>;
}

/**
 * Best-effort extraction of `{ status, error, message }` from a Supabase
 * Functions invoke error. Always returns a usable `message`.
 */
export async function readEdgeError(error: unknown): Promise<EdgeErrorInfo> {
  const ctx = (error as { context?: Response }).context;
  const fallback =
    error instanceof Error ? error.message : String(error ?? "Unknown error");

  if (!ctx || typeof ctx.clone !== "function") {
    return { message: fallback };
  }

  const status = ctx.status;
  try {
    const cloned = ctx.clone();
    const text = await cloned.text();
    if (!text) return { status, message: fallback };

    try {
      const body = JSON.parse(text) as Record<string, unknown>;
      const message =
        (typeof body.message === "string" && body.message) ||
        (typeof body.error === "string" && body.error) ||
        fallback;
      const code = typeof body.error === "string" ? body.error : undefined;
      return { status, code, message, body };
    } catch {
      // Non-JSON body — return the trimmed text.
      return { status, message: text.slice(0, 500) || fallback };
    }
  } catch {
    return { status, message: fallback };
  }
}

/** Convenience helper that returns just the human-readable string. */
export async function readEdgeErrorMessage(error: unknown): Promise<string> {
  const info = await readEdgeError(error);
  return info.message;
}
